import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../pages/routes'
import { useLogout } from '../../hooks/useLogout'
import { useDispatch } from 'react-redux'
import { UserType, selectRole } from '../onboardingContext/OnboardingContextSlice'
import { Role as AdminRole } from '../../types/Role'
import { useSSOClientConfig } from '../sso'

export const SelectRolesPage = () => {
  const [selectedRole, setSelectedRole] = useState<UserType | AdminRole | ''>('')
  const navigate = useNavigate()
  const toast = useToast()
  const logout = useLogout()
  const dispatch = useDispatch()
  const {
    hasFixedOrganization: isClientHasFixedOrganization,
    isPrescriberOnly: isClientPrescriberOnly,
  } = useSSOClientConfig()

  useEffect(() => {
    if (isClientPrescriberOnly) {
      navigate(routes.createPrescriberProfile.path)
    }
  }, [isClientPrescriberOnly])

  const handleRoleSubmit = () => {
    if (!selectedRole) {
      toast({
        title: 'Error',
        description: 'Please select a role to continue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    dispatch(selectRole(selectedRole))
    if (isClientHasFixedOrganization) {
      if (selectedRole === UserType.PRESCRIBER) {
        navigate(routes.createPrescriberProfile.path)
      } else {
        navigate(routes.createOrganizationAdminProfile.path)
      }
    } else {
      navigate(routes.linkOrganization.path)
    }
  }

  return (
    <Container maxW="container.sm" py={24}>
      <Box borderWidth="1px" borderRadius="lg" p={8} boxShadow="lg" bg="white">
        <Flex direction="column" align="center" gap={6}>
          <Heading size="lg" textAlign="center">
            Select a Role to Create User
          </Heading>

          <Text color="gray.600" textAlign="center">
            Please select a role before creating your user profile. This will determine your access
            permissions and features.
          </Text>

          <RadioGroup
            onChange={(value) => setSelectedRole(value as UserType | AdminRole)}
            value={selectedRole}
          >
            <Stack spacing={5} width="full">
              <Radio value={UserType.PRESCRIBER} size="lg" borderColor="gray.300" spacing={4}>
                <Box>
                  <Text fontWeight="semibold">Prescriber</Text>
                  <Text fontSize="sm" color="gray.600">
                    Healthcare professionals who can prescribe medications
                  </Text>
                </Box>
              </Radio>

              {isClientHasFixedOrganization ? (
                <VStack spacing={4} align="flex-start">
                  <Box position="relative" w="full" display="flex" alignItems="center">
                    <Box flex="1" borderTop="1px solid" borderColor="gray.200" />
                    <Text px={3} fontSize="sm" color="gray.500" bg="white" fontWeight="medium">
                      Non-Prescriber
                    </Text>
                    <Box flex="1" borderTop="1px solid" borderColor="gray.200" />
                  </Box>
                  <Radio
                    value={AdminRole.PATIENT_ADMIN}
                    size="lg"
                    borderColor="gray.300"
                    spacing={4}
                  >
                    <Box>
                      <Text fontWeight="semibold">Patient Admin</Text>
                      <Text fontSize="sm" color="gray.600">
                        Manage patient data (non-prescribing role)
                      </Text>
                    </Box>
                  </Radio>

                  <Radio value={AdminRole.RX_ADMIN} size="lg" borderColor="gray.300" spacing={4}>
                    <Box>
                      <Text fontWeight="semibold">RxAdmin</Text>
                      <Text fontSize="sm" color="gray.600">
                        Manage patients and prescription data (non-prescribing role)
                      </Text>
                    </Box>
                  </Radio>
                </VStack>
              ) : (
                <Radio value={UserType.ADMIN} size="lg" borderColor="gray.300" spacing={4}>
                  <Box>
                    <Text fontWeight="semibold">Non-Prescriber</Text>
                    <Text fontSize="sm" color="gray.600">
                      Healthcare professionals who do not prescribe medications
                    </Text>
                  </Box>
                </Radio>
              )}
            </Stack>
          </RadioGroup>

          <Button colorScheme="blue" size="lg" width="full" onClick={handleRoleSubmit} mt={8}>
            Continue
          </Button>
          <Link
            onClick={(e) => {
              e.preventDefault()
              navigate('/login')
              logout()
            }}
            color="blue.500"
            textAlign="center"
            fontSize={'sm'}
            _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            Back to Login Page
          </Link>
        </Flex>
      </Box>
    </Container>
  )
}
